import {
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';
import { useNavigate, useOutletContext, useParams } from 'react-router-dom';
import { MainContext } from '../../../../../../context/MainContext';
import { createScannedDocument } from '../../../../../../services/api/scannedDocuments';
import {
  getProjectTemplateById,
  updateProjectTemplate,
} from '../../../../../../services/api/projectTemplates';
import { concreteTypes } from '../../../../../../constants/general';
import ScannedDocumentsLayout from '../../Commons/ScannedDocumentsLayout';
import SuccessPage from '../../Commons/ScannedDocumentsLayout/components/SuccessPage/index';
import LayoutHeader from '../Commons/LayoutHeader';
import {
  checkDiffTemplate,
  sortRegions,
} from '../../Commons/ScannedDocumentsLayout/utils.index';
import { useIntl } from 'react-intl';
import DeliveryFormFields from '../Commons/DeliveryFormFields';
import {
  deliveryPCFieldGroups,
  deliveryRCFieldGroups,
} from '../Commons/deliveryFieldsGroups';

const CreateDeliveryScannedDoc = () => {
  const navigate = useNavigate();
  const intl = useIntl();
  const [success, setSuccess] = useState(false);
  const [diffTemplate, setDiffTemplate] = useState(false);
  const { setLoading, setError } = useContext(MainContext);
  const { project } = useOutletContext();
  const { templateId, model } = useParams();
  const [initialData, setInitialData] = useState({
    form: {
      templateName: '',
      supplier_id: null,
    },
    regions: [],
  });
  const [template, setTemplate] = useState(null);
  const deliveryFieldGroups = useMemo(() => {
    return model === 'delivery' ? deliveryRCFieldGroups : deliveryPCFieldGroups;
  }, [model]);
  const validableFields = useMemo(
    () =>
      deliveryFieldGroups.mandatory.filter((field) => field !== 'limit_time'),
    [deliveryFieldGroups],
  );

  useEffect(() => {
    const loadInitialData = async () => {
      try {
        setLoading(true);

        if (templateId) {
          const loadedTemplate = await getProjectTemplateById({
            id: templateId,
          });

          if (!loadedTemplate?.data) {
            throw new Error('The template could not be loaded.');
          }

          setTemplate(loadedTemplate.data);

          const templateData = {
            form: {
              templateName:
                loadedTemplate.data?.name || 'Template without a name',
              supplier_id: loadedTemplate.data?.supplier || null,
            },
            regions: loadedTemplate.data?.regions
              ? sortRegions(
                  [
                    ...deliveryFieldGroups.mandatory.filter(
                      (r) => r !== 'limit_time',
                    ),
                    ...deliveryFieldGroups.other.filter(
                      (r) => r !== 'limit_time',
                    ),
                  ],
                  loadedTemplate.data.regions,
                )
              : [],
          };

          setInitialData(templateData);
        }
      } catch (error) {
        console.error('Error loading initial data:', error);
        setError('Error loading initial data');
      } finally {
        setLoading(false);
      }
    };

    loadInitialData();
  }, [
    deliveryFieldGroups.mandatory,
    deliveryFieldGroups.other,
    model,
    setError,
    setLoading,
    templateId,
  ]);

  const createDeliveryScannedDocument = async (
    regions,
    formState,
    fileName,
  ) => {
    try {
      setLoading(true);
      const scannedDocument = await createScannedDocument(
        {
          fileName,
          template: templateId,
          pid: project.id_project,
          camFileName: formState.camFileName,
          delivery_note_data: { ...formState, project: project.id_project },
          regions: regions,
        },
        model,
      );

      if (scannedDocument.data) {
        setSuccess(true);
        if (checkDiffTemplate(regions, template.regions)) {
          setDiffTemplate(regions);
        }
      }
    } catch (error) {
      setError(error);
    } finally {
      setLoading(false);
    }
  };

  useLayoutEffect(() => {
    if (!concreteTypes.includes(model)) {
      navigate('/');
    }
  }, [model, navigate]);

  if (success) {
    return (
      <SuccessPage
        title={intl.formatMessage({
          id: 'pages.ocr.scannedDocuments.delivery.create.success.title',
        })}
        subtitle={intl.formatMessage({
          id: 'pages.ocr.scannedDocuments.delivery.create.success.subtitle',
        })}
        checkDiffTemplate={Boolean(diffTemplate)}
        onBackToOcr={() =>
          navigate(`/app/project/${project.id_project}/quality/ocr`)
        }
        onSaveTemplate={async () => {
          const newTemplate = { ...template, regions: diffTemplate };
          setLoading(true);
          await updateProjectTemplate(newTemplate);
          setLoading(false);
          navigate(`/app/project/${project.id_project}/quality/ocr`, {
            replace: true,
          });
        }}
      />
    );
  }
  return (
    <>
      <ScannedDocumentsLayout
        model={model}
        topFormFields={<DeliveryFormFields model={model} />}
        initialData={initialData}
        fieldGroups={deliveryFieldGroups}
        validableFields={validableFields}
        onFinish={createDeliveryScannedDocument}
        onBack={() =>
          navigate(`/app/project/${project.id_project}/quality/ocr`)
        }
        header={
          <LayoutHeader
            template={initialData?.form?.templateName}
            supplier={initialData?.form?.supplier_id}
          />
        }
      />
    </>
  );
};

export default CreateDeliveryScannedDoc;
